import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Loader2, Printer, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { desktop, desktopPrintAsync } from '@/lib/printTransport'
import { bridgePrintTestTo } from '@/lib/bhooklyAppPrint'
import { describePrintFailure } from '@/lib/printResult'
import { buildPrintHtml } from '@/lib/printKotReceipt'

/**
 * Prove ONE configured printer address, from the screen that configures it.
 *
 * Before this there was exactly one test button in the whole POS, in the
 * phone print-agent card (now PrintAlignmentSetting), and it says on its own
 * face that it "uses the agent's default IP only". So no one could test the address in the receipt-printer box
 * or a station's target — the two values that actually route paper. When
 * Mizaj's printer started refusing connections on 2026-09-10 the staff had no
 * way to check the address themselves, and the diagnosis arrived as a
 * photograph of a three-second Android toast
 * (audit/MIZAJ_TABLET_PRINT_ECONNREFUSED_2026-09-10.md).
 *
 * Works on both shells, because "which printer is this" is the same question
 * on each and only the transport differs:
 *   • Server APK — opens a socket to the ip:port and reports the errno.
 *   • Desktop    — prints to the named Windows device and reports the shell's
 *                  verdict.
 * Hidden in a plain browser, where the OS dialog decides and there is nothing
 * to prove.
 */

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'ok' }
  | { kind: 'sent' } // handed over, but this agent cannot tell us the outcome
  | { kind: 'failed'; detail: string }

export function TestPrinterButton({
  target,
  label = 'Test',
  className,
}: {
  /** ip:port on a tablet, a Windows printer name on the desktop. Blank is allowed. */
  target: string
  /** What this printer is for, shown on the slip. */
  label?: string
  className?: string
}) {
  const isTablet = isBhooklyNativeServerApp()
  const isDesktop = !!desktop()
  const [state, setState] = useState<State>({ kind: 'idle' })
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // Any edit to the address invalidates the previous verdict — leaving a green
  // tick beside a freshly-typed address is worse than showing nothing.
  useEffect(() => {
    setState({ kind: 'idle' })
  }, [target])

  if (!isTablet && !isDesktop) return null

  const set = (s: State) => {
    if (alive.current) setState(s)
  }

  const run = async () => {
    setState({ kind: 'sending' })
    try {
      if (isTablet) {
        const pending = bridgePrintTestTo(target, label)
        if (!pending) {
          set({ kind: 'failed', detail: 'The print agent did not accept the test slip.' })
          return
        }
        const r = await pending
        // An agent below 1.4.0 never reports back. Say "sent" — the paper is
        // the answer — rather than inventing a success or a failure.
        if (!r.supported || r.timedOut) {
          set({ kind: 'sent' })
          return
        }
        const bad = r.jobs.find((j) => !j.ok)
        if (bad) set({ kind: 'failed', detail: describePrintFailure(bad) })
        else set({ kind: 'ok' })
        return
      }

      // Desktop: same slip, through the shell's own printer path so this
      // proves the device the receipts will actually use.
      const html = buildPrintHtml(
        [
          {
            station_id: '',
            station_name: label,
            output_type: 'printer',
            print_location: 'counter',
            printer_target: target,
            payload: testSlipText(target, label),
          },
        ],
        { paperWidthMm: 80, printableAreaMm: 68, fontScale: 1 },
      )
      const r = await desktopPrintAsync(html, { deviceName: target || undefined })
      if (!r) {
        set({ kind: 'failed', detail: 'The desktop app is not available.' })
        return
      }
      if (r.ok) set({ kind: 'ok' })
      else set({ kind: 'failed', detail: r.error || 'The printer rejected the test slip.' })
    } catch (e) {
      set({ kind: 'failed', detail: e instanceof Error ? e.message : String(e) })
    }
  }

  return (
    <div className={cn('mt-2', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void run()}
        disabled={state.kind === 'sending'}
        className="gap-1.5"
      >
        {state.kind === 'sending' ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Printer className="h-4 w-4" aria-hidden />
        )}
        {state.kind === 'sending' ? 'Testing…' : 'Test this printer'}
      </Button>

      {state.kind === 'ok' && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>Printed. This address is correct and reachable.</span>
        </p>
      )}
      {state.kind === 'sent' && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          Sent. This tablet&apos;s app is too old to report back — check the paper.
        </p>
      )}
      {state.kind === 'failed' && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive" role="alert">
          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{state.detail}</span>
        </p>
      )}
    </div>
  )
}

function testSlipText(target: string, label: string): string {
  return [
    '  *** BHOOKLY PRINTER TEST ***',
    '',
    `Target: ${target || '(default printer)'}`,
    `For:    ${label}`,
    new Date().toLocaleString(),
    '',
    'If this slip printed, the address',
    'above is correct and reachable',
    'from this device.',
  ].join('\n')
}
